import { tx } from "../lib/i18n";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowRight, Wallet, CalendarClock, ShieldCheck, Video } from "lucide-react";
import { PageHero } from "../components/site/Layout";
import { HowSteps, TrustStrip } from "../components/home/HeroSections";
import { Formats } from "../components/home/ContentSections";

const Faq = ({ q, a }) => (
  <div className="osk-card p-5"><p className="font-bold text-osk-navy">{q}</p><p className="mt-1 text-sm text-osk-text">{a}</p></div>
);

export function HowItWorks() {
  return (
    <div data-testid="how-page">
      <PageHero title={tx("Hoe het werkt")} sub={tx("Van zoeken tot live meedoen: zo werkt online bijles bij Onskola.")} />
      <HowSteps />
      <TrustStrip />
      <Formats />
      <section id="betalen" className="osk-container py-8">
        <h2 className="mb-4 text-2xl">{tx("Betalen met Sentoo")}</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <Faq q={tx("Geen betaling, geen plek")} a={tx("Je plek wordt pas definitief na een bevestigde betaling. Tijdens het afrekenen houden we je plek 15 minuten vast.")} />
          <Faq q={tx("Wekelijkse lessen")} a={tx("Je betaalt per maand, vóór de maand begint. Je ontvangt 7 dagen vooraf een betaallink, met herinneringen 3 en 1 dag vooraf.")} />
          <Faq q={tx("Betaalbewijs")} a={tx("Na elke betaling ontvang je een betaalbewijs per e-mail. Je kunt ze ook downloaden in je dashboard.")} />
        </div>
      </section>
      <section id="faq" className="osk-container py-8">
        <h2 className="mb-4 text-2xl">{tx("Veelgestelde vragen")}</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Faq q={tx("Wat als ik moet annuleren?")} a={tx("Annuleer je meer dan 24 uur voor de les, dan ontvang je lestegoed. Binnen 24 uur ontvang je geen lestegoed.")} />
          <Faq q={tx("Wat als de docent niet komt?")} a={tx("Annuleert de docent, of is de docent 10 minuten na aanvang niet aanwezig, dan ontvang je automatisch lestegoed.")} />
          <Faq q={tx("Heeft mijn kind een account nodig?")} a={tx("Nee. Je kind doet mee via jouw account of via een persoonlijke link die alleen voor die les en dat kind werkt.")} />
          <Faq q={tx("Worden lessen opgenomen?")} a={tx("Nee. Alle lessen zijn live en worden niet opgenomen. Je ziet wel de aanwezigheid van je kind per les.")} />
        </div>
      </section>
    </div>
  );
}

const BENEFITS = [
  { icon: Wallet, t: "Extra inkomen", d: "Verdien per kind per les. Maandelijks overzicht en uitbetaling." },
  { icon: CalendarClock, t: "Flexibel", d: "Kies zelf je vakken, lesvormen en tijden." },
  { icon: Video, t: "Alles ingebouwd", d: "Live videolessen met schermdelen, direct op het platform." },
  { icon: ShieldCheck, t: "Professioneel", d: "Wij regelen betalingen, aanwezigheid en communicatie." },
];

export function ForTeachers() {
  return (
    <div data-testid="for-teachers-page">
      <PageHero title={tx("Geef online bijles bij Onskola")} sub={tx("Deel je kennis met leerlingen in de Dutch Caribbean. Flexibel, vanuit huis en met de steun van een professioneel platform.")}>
        <Link to="/docent-worden" className="osk-btn-primary mt-6" data-testid="for-teachers-apply-btn">{tx("Word docent")} <ArrowRight className="h-5 w-5" /></Link>
      </PageHero>
      <section id="voordelen" className="osk-container grid gap-4 py-8 sm:grid-cols-2 lg:grid-cols-4">
        {BENEFITS.map(({ icon: Icon, t, d }) => (
          <div key={t} className="osk-card p-6"><Icon className="mb-3 h-8 w-8 text-osk-blue" /><p className="font-extrabold text-osk-navy">{tx(t)}</p><p className="mt-1 text-sm text-osk-text">{tx(d)}</p></div>
        ))}
      </section>
      <section className="osk-container py-8">
        <h2 className="mb-4 text-2xl">{tx("Zo word je docent")}</h2>
        <ol className="space-y-3">
          {[tx("Meld je aan en accepteer de Algemene Voorwaarden voor Docenten."), tx("Upload je ID, diploma's en verklaring omtrent gedrag (alleen zichtbaar voor ons team)."),
            tx("Wij beoordelen je aanmelding."), tx("Na goedkeuring maak je lessen aan en ontvang je boekingen.")].map((s, i) => (
            <li key={s} className="flex items-center gap-3"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-osk-orange font-extrabold text-white">{i + 1}</span><span className="text-osk-text">{s}</span></li>
          ))}
        </ol>
        <p className="mt-6 flex items-center gap-2 text-sm text-osk-text"><CheckCircle2 className="h-5 w-5 text-emerald-600" />{tx("Contactgegevens in profielen en lessen zijn niet toegestaan; alle communicatie en betalingen lopen via het platform.")}</p>
      </section>
    </div>
  );
}

const LEGAL = {
  "/voorwaarden/ouders": ["Algemene Voorwaarden voor Ouders", "Versie 1.0"],
  "/voorwaarden/docenten": ["Algemene Voorwaarden voor Docenten", "Versie 1.0"],
  "/privacy": ["Privacybeleid", "Versie 1.0"],
};

export function Legal() {
  const { pathname } = useLocation();
  const [title, version] = LEGAL[pathname] || LEGAL["/privacy"];
  return (
    <div data-testid="legal-page">
      <PageHero title={tx(title)} sub={`${tx(version)} · ${tx("Placeholder-tekst. De definitieve juridische tekst volgt.")}`} />
      <div className="osk-container max-w-3xl space-y-4 pb-10 text-osk-text">
        {[tx("1. Toepasselijkheid"), tx("2. Boekingen en betaling"), tx("3. Annuleringen en lestegoed"), tx("4. Gedragsregels"), tx("5. Privacy en gegevens"), tx("6. Slotbepalingen")].map((h) => (
          <div key={h}><h3 className="mb-1 text-lg">{tx(h)}</h3><p className="text-sm">{tx("Deze sectie is een placeholder. Hier komt de definitieve tekst.")}</p></div>
        ))}
      </div>
    </div>
  );
}
